/*
  Material table (UI overhaul wave 2, screen 05 ideas 3, 5 and 6).

  - One "Variations Σ" column and one "Delivered" column instead of a column per VO and per delivery note;
    each opens to show its VO-001 … / DN columns (oldest first), so the table keeps its width however many
    VOs and DNs a project collects.
  - Quantities (BOQ, Sub BOQ qty, delivered per DN) are edited in place: an edited cell gets an amber
    border + dot and joins the page's "N unsaved quantity changes · Discard · Save changes" bar. Nothing
    is saved per row any more.
  - Clicking a row opens its history underneath (VOs, delivery notes, site requests, site needs).
  - Deleting lives in the row ⋯ menu (with a confirmation and Undo) — no red buttons in rows.

  variant 'detail' (the BOQ lines) or 'summary' (Main BOQ of a Sub BOQ project: read-only totals).
*/
import { Fragment } from 'react';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { RowActions } from '@/components/ui/row-actions';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { MONO } from './ProjectUi';
import { MaterialProgressCell } from './MaterialPanels';
import { dnDisplay, qtyText, stockCellFor } from './materialModel';

const TH = 'h-10 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left align-middle text-[11.5px] font-bold text-ink-3';
const TD = 'border-b border-line-2 px-3 py-2 align-middle text-[13px]';
const NUM = 'text-right tabular-nums';
// 05 #5 preview: Remaining / Progress of a row whose BOQ qty was edited (not saved yet) — amber, like the edited cell.
const PREVIEW = 'bg-status-amber-bg';

export function QtyInput({ value, changed, invalid, error, onChange, label, disabled, className, testId }) {
  return (
    <span className={cn('relative inline-flex', className)}>
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        aria-invalid={invalid || undefined}
        title={invalid ? error : undefined}
        value={value ?? ''}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled}
        data-row-click="ignore"
        data-testid={testId}
        className={cn(
          'h-8 w-[76px] rounded-lg border bg-transparent px-2 text-right text-[13px] tabular-nums text-ink outline-none transition-colors',
          'focus:border-[color:var(--brand)] focus:bg-surface focus:shadow-focus disabled:cursor-default',
          changed ? 'border-status-amber bg-[color:color-mix(in_srgb,var(--status-amber-bg)_50%,transparent)] font-semibold' : 'border-transparent hover:border-line',
          invalid && 'border-status-red bg-[color:color-mix(in_srgb,var(--status-red-bg)_50%,transparent)]',
        )}
      />
      {changed || invalid ? (
        <span
          className={cn('pointer-events-none absolute -right-1 -top-1 h-2 w-2 rounded-full ring-2 ring-[color:var(--surface)]', invalid ? 'bg-status-red' : 'bg-status-amber')}
          aria-hidden="true"
        />
      ) : null}
    </span>
  );
}

function GroupHeader({ label, codes, open, onToggle, count, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="group/head inline-flex flex-col items-end rounded px-0.5 text-right leading-tight hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={testId}
      title={open ? `Hide the ${count} columns` : `Show each of the ${count} columns`}
    >
      <span className="inline-flex items-center gap-1">
        {count ? <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" /> : null}
        {label}
      </span>
      {codes ? <span className={cn(MONO, 'text-[10.5px] font-semibold text-ink-3')}>{codes}</span> : null}
    </button>
  );
}

const codeRange = (codes = []) => {
  if (!codes.length) return '';
  if (codes.length === 1) return codes[0];
  if (codes.length === 2) return `${codes[0]}, ${codes[1]}`;
  return `${codes[0]}…${codes[codes.length - 1].replace(/^.*?(\d+)$/, '$1')}`;
};

const shortStore = (location) => String(location || '').replace(/\s+Store$/i, '');

/** 05 #9: "Dubai Store 6" (most available first), green when the stores cover what is still to deliver. */
function StockCell({ row, stock }) {
  const cell = stockCellFor(row, stock);
  if (!cell) return <td className={cn(TD, 'pl-2 pr-1 text-ink-4')}>—</td>;
  return (
    <td className={cn(TD, 'whitespace-nowrap pl-2 pr-1 text-[12.5px]')} data-testid={`material-stock-${row.id}`}>
      <span
        className={cn(
          cell.tone === 'muted' && 'text-ink-3',
          cell.tone === 'green' && 'font-semibold text-status-green-ink',
          cell.tone === 'default' && 'text-ink-2',
        )}
        title={`${cell.detail}${cell.covers ? ' — enough for what is still to deliver' : ''}`}
      >
        {cell.label}
      </span>
      {cell.covers ? <span className="sr-only"> — enough for what is still to deliver</span> : null}
    </td>
  );
}

export function MaterialTable({
  variant = 'detail',
  entries = [],
  voColumns = [],
  dnColumns = [],
  showVoDetail = false,
  onToggleVoDetail,
  showDnDetail = false,
  onToggleDnDetail,
  allowSubBoq = false,
  canEdit = false,
  selection,
  qty,
  subBoqOptions = [],
  expandedRowIds,
  onToggleRow,
  renderHistory,
  rowMenu,
  onOpenDeliveryNote,
  onToggleGroup,
  drag,
  footer,
  testId,
  stock,
}) {
  const detail = variant === 'detail';
  // From a 1366px window the table has room for the Unit column and wider Description / Progress; below it (the
  // 248px sidebar leaves a ~966px card at 1280) they tighten so every column fits. The Sub BOQ column (an input)
  // always takes that room, so Sub BOQ lines keep the tight layout at every width.
  const roomy = !(detail && allowSubBoq);
  // 05 #9: the "In stock" column (store stock of the part) — shown once GET /material-stock answered.
  const showStock = Boolean(stock);
  const rowEntries = entries.filter((entry) => entry.type === 'row');
  const voCodes = voColumns.map((column) => column.vo_number || 'VO');
  // Bare numbers ('851') get a 'DN ' prefix so they don't read like quantities (display only).
  const dnCodes = dnColumns.map((column) => dnDisplay(column.dn_number) || 'DN');
  const columnCount = (detail ? 2 : 0) + 3 + (detail && allowSubBoq ? 1 : 0) + 1
    + (showVoDetail ? voColumns.length : 0) + 1 + (showDnDetail ? dnColumns.length : 0) + 1 + 2 + (showStock ? 1 : 0) + (detail ? 1 : 0);

  const renderRow = (entry) => {
    const { row } = entry;
    const isOpen = Boolean(detail && expandedRowIds?.has(row.id));
    const selected = Boolean(selection?.selectedIds?.includes(row.id));
    const boq = detail ? qty?.boq(row) : null;
    const subBoq = detail && allowSubBoq ? qty?.subBoq(row) : null;
    return (
      <Fragment key={row.id}>
        <tr
          className={cn(
            'group/row transition-colors',
            detail && 'cursor-pointer hover:bg-[color:color-mix(in_srgb,var(--surface-hover)_60%,transparent)]',
            selected && 'bg-[color:color-mix(in_srgb,var(--brand-soft)_40%,transparent)]',
            isOpen && 'bg-[color:color-mix(in_srgb,var(--surface-2)_70%,transparent)]',
            drag?.draggingId === row.id && 'opacity-40',
          )}
          onClick={detail ? (event) => {
            if (event.target.closest('button, a, input, [role="checkbox"], [data-row-click="ignore"]')) return;
            onToggleRow?.(row.id);
          } : undefined}
          onDragOver={drag?.active ? (event) => event.preventDefault() : undefined}
          onDrop={drag?.active ? (event) => { event.preventDefault(); drag.onDrop(row.id); } : undefined}
          data-testid={detail ? `material-row-${row.id}` : `main-boq-row-${row.id}`}
        >
          {detail ? (
            <td className={cn(TD, 'w-10 pr-0')} data-row-click="ignore" onMouseDown={(event) => selection?.onPointer?.(event)}>
              <TableCheckbox
                checked={selected}
                onCheckedChange={(checked) => selection?.onToggle(row.id, checked)}
                aria-label={`Select ${row.part_number || row.description || 'line'}`}
              />
            </td>
          ) : null}
          {detail ? (
            <td className={cn(TD, 'w-[68px] whitespace-nowrap pl-1 pr-1 text-ink-3')}>
              <span className="inline-flex items-center gap-0.5">
                {canEdit && drag ? (
                  <button
                    type="button"
                    draggable
                    onDragStart={() => drag.onDragStart(row.id)}
                    onDragEnd={drag.onDragEnd}
                    className="grid h-6 w-5 cursor-grab place-items-center rounded text-ink-4 opacity-0 transition-opacity hover:text-ink-2 focus-visible:opacity-100 group-hover/row:opacity-100"
                    aria-label={`Drag ${row.part_number || 'line'} to reorder`}
                    title="Drag to reorder"
                  >
                    <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => onToggleRow?.(row.id)}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} history of ${row.part_number || row.description || 'line'}`}
                  className="inline-flex h-6 items-center gap-0.5 rounded px-0.5 text-[12.5px] tabular-nums hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {isOpen ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                  {entry.number}
                </button>
              </span>
            </td>
          ) : null}
          <td className={cn(TD, MONO, 'whitespace-nowrap text-[12.5px] font-semibold text-ink')}>{row.part_number || '—'}</td>
          <td className={cn(TD, 'min-w-[112px] text-ink-2', roomy && (showStock ? 'min-[1366px]:min-w-[148px]' : 'min-[1366px]:min-w-[180px]'))}>{row.description || '—'}</td>
          {detail && allowSubBoq ? (
            <td className={cn(TD, 'min-w-[112px]')}>
              {canEdit ? (
                <span className="relative inline-flex">
                  <input
                    list="material-sub-boq-options"
                    value={subBoq?.value ?? ''}
                    onChange={(event) => qty?.onSubBoqChange(row.id, event.target.value)}
                    aria-label={`Sub BOQ of ${row.part_number || 'line'}`}
                    placeholder="Sub BOQ"
                    data-row-click="ignore"
                    className={cn(
                      'h-8 w-[112px] rounded-lg border bg-transparent px-2 text-[13px] outline-none focus:border-[color:var(--brand)] focus:bg-surface',
                      subBoq?.changed ? 'border-status-amber bg-[color:color-mix(in_srgb,var(--status-amber-bg)_50%,transparent)]' : 'border-transparent hover:border-line',
                    )}
                  />
                  {subBoq?.changed ? <span className="pointer-events-none absolute -right-1 -top-1 h-2 w-2 rounded-full bg-status-amber ring-2 ring-[color:var(--surface)]" aria-hidden="true" /> : null}
                </span>
              ) : <span className="text-ink-2">{row.sub_boq || '—'}</span>}
            </td>
          ) : null}
          <td className={cn(TD, 'hidden text-ink-3', roomy && 'min-[1366px]:table-cell')}>{row.unit || 'pcs'}</td>
          <td className={cn(TD, NUM, 'whitespace-nowrap')}>
            {detail && canEdit ? (
              <QtyInput
                value={boq?.value}
                changed={boq?.changed}
                invalid={boq?.invalid}
                error={boq?.error}
                onChange={(value) => qty?.onBoqChange(row.id, value)}
                label={`BOQ quantity for ${row.part_number || row.description || 'line'}`}
                testId={`material-boq-qty-${row.id}`}
              />
            ) : <span className="font-semibold text-ink">{qtyText(row.boq_qty)}</span>}
            {/* Below 1366px the Unit column is hidden: the unit follows the BOQ number. */}
            <span className={cn('ml-1 text-[11.5px] text-ink-4', roomy && 'min-[1366px]:hidden')}>{row.unit || 'pcs'}</span>
          </td>
          {showVoDetail ? voColumns.map((variation) => (
            <td key={variation.id} className={cn(TD, NUM, 'bg-[color:color-mix(in_srgb,var(--status-violet-bg)_30%,transparent)] text-ink-2')}>
              {row.variationById?.[variation.id] ? qtyText(row.variationById[variation.id]) : <span className="text-ink-4">0</span>}
            </td>
          )) : null}
          <td className={cn(TD, NUM)} data-testid={detail ? `material-vo-total-${row.id}` : `main-boq-vo-total-${row.id}`}>
            {Number(row.variationTotal || 0) !== 0
              ? <span className="font-semibold text-status-violet-ink">{row.variationTotal > 0 ? '+' : ''}{qtyText(row.variationTotal)}</span>
              : <span className="text-ink-4">0</span>}
          </td>
          {showDnDetail ? dnColumns.map((column) => {
            if (!detail) {
              return (
                <td key={column.id} className={cn(TD, NUM, 'bg-[color:color-mix(in_srgb,var(--brand-soft)_30%,transparent)] text-ink-2')} data-testid={`main-boq-dn-${row.id}-${column.id}`}>
                  {qtyText(row.deliveredByDn?.[column.id] || 0)}
                </td>
              );
            }
            const merged = (column.note_ids || []).length > 1;
            const cell = qty?.dn(column, row);
            if (cell?.readOnly) {
              // The note is for another Sub BOQ: this line can never be delivered on it.
              return (
                <td key={column.id} className={cn(TD, NUM, 'bg-[color:color-mix(in_srgb,var(--brand-soft)_30%,transparent)]')}>
                  <span className="text-ink-4" title={cell.readOnlyReason || undefined} aria-hidden="true">—</span>
                  {cell.readOnlyReason ? <span className="sr-only">{cell.readOnlyReason}</span> : null}
                </td>
              );
            }
            return (
              <td key={column.id} className={cn(TD, NUM, 'bg-[color:color-mix(in_srgb,var(--brand-soft)_30%,transparent)]')}>
                {canEdit && !merged ? (
                  <QtyInput
                    value={cell?.value}
                    changed={cell?.changed}
                    invalid={cell?.invalid}
                    error={cell?.error}
                    onChange={(value) => qty?.onDnChange(column, row.id, value)}
                    label={`Delivered quantity for ${row.part_number || row.description || 'line'} in ${column.dn_number}`}
                  />
                ) : <span className="text-ink-2" title={merged ? 'Several delivery notes share this number — edit them from the delivery note' : undefined}>{qtyText(cell?.value ?? 0)}</span>}
              </td>
            );
          }) : null}
          <td
            className={cn(TD, NUM, 'font-semibold text-ink')}
            data-testid={detail ? `material-delivered-total-${row.id}` : `main-boq-delivered-total-${row.id}`}
          >
            {qtyText(row.deliveredTotal)}
          </td>
          <td className={cn(TD, 'w-[104px]', roomy && (showStock ? 'min-[1366px]:w-[124px]' : 'min-[1366px]:w-[150px]'), row.boqPreview && PREVIEW)} title={row.boqPreview ? 'With the unsaved BOQ quantity' : undefined}>
            <MaterialProgressCell row={row} compact={showStock} tight={!roomy} testId={detail ? `material-progress-${row.id}` : `main-boq-progress-${row.id}`} />
          </td>
          <td className={cn(TD, NUM, 'whitespace-nowrap', row.boqPreview && PREVIEW)} data-preview={row.boqPreview ? 'true' : undefined}>
            <span className={cn('block', row.remaining > 0 ? 'font-bold text-status-amber-ink' : 'text-ink-3')}>{qtyText(row.remaining)}</span>
            {row.variationNeeded > 0 ? (
              <StatusPill tone="red" size="sm" label={`VO needed +${formatNumber(row.variationNeeded, { maxDecimals: 2 })}`} className="mt-1" />
            ) : null}
          </td>
          {showStock ? <StockCell row={row} stock={stock} /> : null}
          {detail ? (
            <td className={cn(TD, 'w-11 pl-0 pr-2 text-right')} data-row-click="ignore">
              {rowMenu ? <RowActions size="sm" label={`Actions for ${row.part_number || row.description || 'line'}`} items={rowMenu(row)} data-testid={`material-row-actions-${row.id}`} /> : null}
            </td>
          ) : null}
        </tr>
        {isOpen && renderHistory ? (
          <tr className="bg-[color:color-mix(in_srgb,var(--surface-2)_70%,transparent)]" data-testid={`material-history-${row.id}`}>
            <td className="border-b border-line-2 px-4 py-2.5" colSpan={columnCount}>{renderHistory(row)}</td>
          </tr>
        ) : null}
      </Fragment>
    );
  };

  const allSelected = Boolean(selection && rowEntries.length && rowEntries.every((entry) => selection.selectedIds.includes(entry.row.id)));
  const someSelected = Boolean(selection && rowEntries.some((entry) => selection.selectedIds.includes(entry.row.id)));

  return (
    // relative: the scroller is the containing block of the sr-only labels inside it — without it they are
    // positioned against the page and widen it (phone page 1025px wide, desktop sideways scroll).
    <div className="relative overflow-x-auto" data-testid={testId}>
      {detail && allowSubBoq ? (
        <datalist id="material-sub-boq-options">
          {subBoqOptions.map((option) => <option key={option} value={option} />)}
        </datalist>
      ) : null}
      <TooltipProvider delayDuration={150}>
        <table className="w-full min-w-[860px] border-separate border-spacing-0">
          <thead>
            <tr>
              {detail ? (
                <th className={cn(TH, 'w-10 pr-0')}>
                  {selection ? (
                    <TableCheckbox
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      onCheckedChange={(checked) => selection.onToggleAll(checked)}
                      aria-label="Select all BOQ lines"
                    />
                  ) : null}
                </th>
              ) : null}
              {detail ? <th className={cn(TH, 'w-[68px] pl-2')}>#</th> : null}
              <th className={TH}>Part No</th>
              <th className={TH}>Description</th>
              {detail && allowSubBoq ? <th className={TH}>Sub BOQ</th> : null}
              <th className={cn(TH, 'hidden', roomy && 'min-[1366px]:table-cell')}>Unit</th>
              <th className={cn(TH, NUM)}>BOQ</th>
              {showVoDetail ? voColumns.map((variation) => (
                <th key={variation.id} className={cn(TH, NUM, MONO, 'bg-[color:color-mix(in_srgb,var(--status-violet-bg)_60%,transparent)] text-[11px] text-status-violet-ink')} title={variation.description || undefined}>
                  {variation.vo_number || 'VO'}
                </th>
              )) : null}
              <th className={cn(TH, NUM)}>
                <GroupHeader
                  label={showVoDetail ? 'VO total' : 'Variations Σ'}
                  codes={showVoDetail ? '' : codeRange(voCodes)}
                  open={showVoDetail}
                  count={voColumns.length}
                  onToggle={voColumns.length ? onToggleVoDetail : undefined}
                  testId={detail ? 'material-vo-toggle' : 'main-boq-vo-toggle'}
                />
              </th>
              {showDnDetail ? dnColumns.map((column) => (
                <th key={column.id} className={cn(TH, NUM, 'bg-[color:color-mix(in_srgb,var(--brand-soft)_60%,transparent)]')}>
                  {detail ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onOpenDeliveryNote?.(column.primary_note_id)}
                          className={cn(MONO, 'rounded text-[11px] font-bold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
                          aria-label={`Open delivery note ${column.dn_number}`}
                        >
                          {dnDisplay(column.dn_number)}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap">
                        {(column.notes || []).length ? column.notes.join('\n\n') : 'No notes'}
                      </TooltipContent>
                    </Tooltip>
                  ) : <span className={cn(MONO, 'text-[11px]')}>{dnDisplay(column.dn_number)}</span>}
                </th>
              )) : null}
              <th className={cn(TH, NUM)}>
                <GroupHeader
                  label={showDnDetail ? 'Delivered total' : 'Delivered'}
                  codes={showDnDetail ? '' : codeRange(dnCodes)}
                  open={showDnDetail}
                  count={dnColumns.length}
                  onToggle={dnColumns.length ? onToggleDnDetail : undefined}
                  testId={detail ? 'material-dn-toggle' : 'main-boq-dn-toggle'}
                />
              </th>
              <th className={cn(TH, 'w-[104px]', roomy && (showStock ? 'min-[1366px]:w-[124px]' : 'min-[1366px]:w-[150px]'))}>Progress</th>
              <th className={cn(TH, NUM)}>Remaining</th>
              {showStock ? (
                <th className={cn(TH, 'min-w-[90px] pl-2 pr-1')} data-testid={detail ? 'material-stock-header' : undefined}>
                  <span className="block">In stock</span>
                  {stock.locations?.length ? (
                    <span className="block whitespace-normal text-[10.5px] font-semibold leading-tight text-ink-4">{stock.locations.map(shortStore).join(' · ')}</span>
                  ) : null}
                </th>
              ) : null}
              {detail ? <th className={cn(TH, 'w-11')}><span className="sr-only">Actions</span></th> : null}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              if (entry.type !== 'header') return renderRow(entry);
              return (
                <tr key={`group-${entry.label}`} className="bg-surface-2">
                  <td colSpan={columnCount} className="border-b border-line-2 px-3 py-1.5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => onToggleGroup?.(entry.label)}
                        aria-expanded={!entry.collapsed}
                        className="inline-flex items-center gap-1.5 rounded text-[13px] font-bold text-ink hover:text-[color:var(--brand-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {entry.collapsed ? <ChevronRight className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                        <span>{entry.label}</span>
                        <span className="font-normal text-ink-3">({entry.count})</span>
                      </button>
                      {entry.qty ? (
                        <span className="inline-flex items-center gap-2 text-[12px] text-ink-3">
                          Sub BOQ qty
                          {canEdit ? (
                            <QtyInput
                              value={entry.qty.value}
                              changed={entry.qty.changed}
                              invalid={entry.qty.invalid}
                              error={entry.qty.error}
                              onChange={(value) => qty?.onSubBoqQtyChange(entry.label, value)}
                              label={`Sub BOQ quantity of ${entry.label}`}
                            />
                          ) : <b className="font-semibold text-ink">{entry.qty.value}</b>}
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!entries.length ? (
              <tr><td colSpan={columnCount} className="px-4 py-8 text-center text-[13px] text-ink-3">No lines match.</td></tr>
            ) : null}
          </tbody>
        </table>
      </TooltipProvider>
      {footer}
    </div>
  );
}
